/**
 * rental_client.ts — RENTALS, the client's pure half (the interiors programme, slice R, 2026-10-01). No three, no DOM —
 * the console, the rentals module, `deed_home` and the harnesses all read it.
 *
 *   unitClaimsOf     — WHAT THE OWNER'S CLIENT CLAIMS when it lists a room: the planner's units of the visit's own
 *                      descriptor (rect, the threshold anchor's door point and standing point, the room's keyed pack
 *                      pieces as the seat view keys them). The server bounds the claim (`rentals.unitClaimProblem`),
 *                      binds it to the layout pin's digest and — where its own room plan knows the unit — checks it
 *                      against that plan (server lib/rental_ops.ts `resolveUnitClaim`).
 *   the let store    — which rooms of which building are LET right now (globalThis-backed: one store whatever module
 *                      instance asks). `AtlasRentalsModule` fills it from the replicated `rental_lease` rows;
 *                      `deed_home.deedResidentSpot` reads it so no versona resident is bedded in a tenant's room.
 *   leaseStatus      — one wording for a lease's clock (paid / grace / notice) on the console, the Town tab, the plate.
 */
import type { InteriorDescriptor } from './interior_descriptor.js';
import type { SeatView } from './interior_wiring.js';
import type { ResidentRoom } from './home_layout.js';
import { unitNumber as plateNumber } from './interior_kit_props.js';
import { UNIT_KEYS_MAX, lapseDecision, refundAt, spanLabel, unitHolds, type UnitClaim, type UnitRect } from './rentals.js';

// ─── The claim ───────────────────────────────────────────────────────────────────────────────────────────────────────

/** Every unit of a planned descriptor as the claim `listRentalUnit` takes. `origin` = the interior's origin in the
 *  structure's own frame (0, 0 for a well-pivoted deed model — the deed set is). A legacy room (layout 0) has no unit. */
export function unitClaimsOf(d: InteriorDescriptor, view: SeatView, origin: { ox: number; oz: number } = { ox: 0, oz: 0 }): UnitClaim[] {
  const out: UnitClaim[] = [];
  for (const f of [...d.floors].sort((a, b) => a.floor - b.floor)) {
    const seated = view.floors.find((v) => v.floor === f.floor)?.pieces ?? [];
    for (const u of f.units) {
      const door = f.anchors.find((a) => a.id === u.threshold);
      const cx = (u.rect.x0 + u.rect.x1) / 2, cz = (u.rect.z0 + u.rect.z1) / 2;
      const keys: string[] = [];
      for (const p of seated) {
        if (!p.storageKey || !u.pieces.includes(p.slot) || keys.includes(p.storageKey)) continue;
        if (keys.length < UNIT_KEYS_MAX) keys.push(p.storageKey);
      }
      out.push({
        unitId: u.unitId, kind: u.kind, floor: u.floor,
        x0: u.rect.x0, z0: u.rect.z0, x1: u.rect.x1, z1: u.rect.z1,
        tx: door ? door.x : cx, tz: door ? door.z : cz, ax: door ? door.ax : cx, az: door ? door.az : cz,
        ox: origin.ox, oz: origin.oz, keys,
      });
    }
  }
  return out;
}
/** The room's number EXACTLY as the visit's own door plate reads it (slice F's `interior_kit_props.unitNumber`:
 *  the floor, then the room on it) — `unit/f1/0` → "101". One wording: the prompt, the console and the plate agree. */
export function unitNumber(unitId: string): string {
  const m = /^unit\/f(\d{1,2})\/(\d{1,2})$/.exec(unitId);
  return m ? plateNumber(Number(m[1]), Number(m[2])) : unitId;
}

// ─── The let store ───────────────────────────────────────────────────────────────────────────────────────────────────

export interface LetUnit extends UnitRect { unitId: string; leaseId: bigint; tenantHex: string; tenantName: string }
interface RentalStore { let: Map<string, LetUnit[]>; version: number }
const G = globalThis as unknown as { __atlasRentalStore?: RentalStore };
function store(): RentalStore { return (G.__atlasRentalStore ??= { let: new Map(), version: 0 }); }

/** Replace the whole picture (the module rebuilds it when the lease rows move). Keys are building ids: `deed:<id>`. */
export function setLetUnits(byBuilding: Map<string, LetUnit[]>): void {
  const s = store();
  s.let = byBuilding; s.version++;
}
export function letUnitsOf(buildingId: string): readonly LetUnit[] { return store().let.get(buildingId) ?? []; }
export function letStoreVersion(): number { return store().version; }
/** The let room a point of a building's interior frame lies in (null: none). */
export function letUnitAt(buildingId: string, floor: number, x: number, z: number, pad = 0): LetUnit | null {
  for (const u of letUnitsOf(buildingId)) if (unitHolds(u, floor, x, z, pad)) return u;
  return null;
}
/**
 * NO RESIDENT IN A TENANT'S ROOM: the room a versona resident chooses from, less every bed, seat, standing point and
 * rug that lies in a let unit. The SAME object comes back when nothing is let (the common case allocates nothing).
 */
export function withoutLetUnits(buildingId: string, room: ResidentRoom): ResidentRoom {
  const units = letUnitsOf(buildingId);
  if (units.length === 0) return room;
  const out = (floor: number, x: number, z: number): boolean => !units.some((u) => unitHolds(u, floor, x, z, 0.15));
  return {
    sockets: room.sockets.filter((s) => out(s.floor, s.x, s.z)),
    stands: room.stands.filter((s) => out(s.floor, s.x, s.z)),
    rugs: room.rugs.filter((s) => out(s.floor, s.x, s.z)),
    solids: room.solids,
  };
}

// ─── One wording for a lease's clock ─────────────────────────────────────────────────────────────────────────────────

export interface LeaseFacts { sinceS: number; paidDays: number; releasedDays: number; rentPerDay: number; paidUntilS: number; endsAtS: number; state: string }
export interface LeaseStatus {
  phase: 'paid' | 'grace' | 'notice' | 'ended';
  /** "paid for 2 d 3 h" / "rent due — 5 h of grace left" / "notice: ends in 1 d 2 h" / "ended (left)". */
  line: string;
  /** What leaving now would return from escrow. */
  refund: number;
}
export function leaseStatus(l: LeaseFacts, nowS: number): LeaseStatus {
  if (l.state !== 'live') return { phase: 'ended', line: `ended (${l.state.replace(/^ended:/, '')})`, refund: 0 };
  const refund = refundAt({ sinceS: l.sinceS, paidDays: l.paidDays, releasedDays: l.releasedDays, rentPerDay: l.rentPerDay }, nowS);
  const v = lapseDecision(nowS, l.paidUntilS);
  if (l.endsAtS > 0) {
    const tail = v.phase === 'paid' ? '' : ' — rent due';
    return { phase: 'notice', line: `notice given: the lease ends in ${spanLabel(l.endsAtS - nowS)}${tail}`, refund };
  }
  if (v.phase === 'paid') return { phase: 'paid', line: `paid for ${spanLabel(l.paidUntilS - nowS)}`, refund };
  return { phase: 'grace', line: `rent due — ${spanLabel(Math.max(0, v.releaseAtS - nowS))} of grace left, then the room is released`, refund };
}

// ─── The replicated rows, as the client reads them (structural: no SDK type crosses into a pure lib) ─────────────────

export interface IdLike { isEqual(other: never): boolean; toHexString(): string }
export interface TsLike { microsSinceUnixEpoch: bigint }
export interface RentalUnitRow {
  unitRowId: bigint; structureId: bigint; unitId: string; listed: boolean; rentPerDay: number; notes: string;
  layoutRevision: number; digest: string; kind: string; floor: number;
  x0: number; z0: number; x1: number; z1: number; tx: number; tz: number; ax: number; az: number; ox: number; oz: number;
  keysJson: string; source: string;
}
export interface RentalLeaseRow {
  leaseId: bigint; unitRowId: bigint; structureId: bigint; unitId: string; tenant: IdLike; tenantName: string; landlord: IdLike;
  rentPerDay: number; since: TsLike; paidDays: number; releasedDays: number; paidUntil: TsLike; heldSov: bigint;
  noticeAt: TsLike; endsAt: TsLike; state: string; endedAt: TsLike;
}
export interface RentalReceiptRow { receiptId: bigint; leaseId: bigint; structureId: bigint; unitId: string; kind: string; amountSov: bigint; feeSov: bigint; detail: string; at: TsLike }

const secOf = (t: TsLike): number => Number(t.microsSinceUnixEpoch / 1_000_000n);
export function leaseFacts(l: RentalLeaseRow): LeaseFacts {
  return {
    sinceS: secOf(l.since), paidDays: l.paidDays, releasedDays: l.releasedDays, rentPerDay: l.rentPerDay,
    paidUntilS: secOf(l.paidUntil), endsAtS: l.endsAt.microsSinceUnixEpoch === 0n ? 0 : secOf(l.endsAt), state: l.state,
  };
}
export function unitKeys(u: { keysJson: string }): string[] {
  try { const k = JSON.parse(u.keysJson) as unknown; return Array.isArray(k) ? k.filter((x): x is string => typeof x === 'string') : []; } catch { return []; }
}
/** What a room's own plate reads under its number: the tenant's name, "TO LET · 40", or nothing (not listed). */
export function plateText(u: { listed: boolean; rentPerDay: number }, lease: { tenantName: string } | null): string {
  if (lease) return lease.tenantName.slice(0, 14).toUpperCase();
  return u.listed ? `TO LET · ${u.rentPerDay}` : '';
}
/** The threshold prompt: what E does at a room's door ('' = no E here). */
export function thresholdLabel(u: { unitId: string; listed: boolean; rentPerDay: number }, lease: { tenantName: string } | null, who: 'tenant' | 'owner' | 'visitor', statusLine = ''): { label: string; act: boolean } {
  const n = unitNumber(u.unitId);
  if (lease) {
    if (who === 'tenant') return { label: `E — your room ${n} · ${statusLine}`, act: true };
    return { label: `Room ${n} — let to ${lease.tenantName}`, act: false };
  }
  if (!u.listed) return { label: '', act: false };
  if (who === 'owner') return { label: `Room ${n} — to let at ${u.rentPerDay} ⓒ a day (manage it at the console)`, act: false };
  return { label: `E — rent room ${n} · ${u.rentPerDay} ⓒ a day`, act: true };
}
